import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useTagContext } from '../../contexts/TagContext'
import { useUser } from '../../contexts/UserContext'
import { useAutoSave } from '../../hooks/useAutoSave'
import { projectService } from '../../services/projectService'
import {
  getRelatedBrokerName,
  getRelatedContractorName,
  tagService,
} from '../../services/tagService'
import type { Attachment, Project } from '../../types/project'
import type { CreateTagData, Tag } from '../../types/tag'
import type { WorkOrder } from '../../types/workorder'
import DashboardAddModal from '../dashboard/DashboardAddModal'
import ContactInfoStep from './ContactInfoStep'
import JobDetailsStep from './JobDetailsStep'
import TagConfirmation from './TagConfirmation'

interface AddTagModalProps {
  workorder?: { id: string } & Partial<WorkOrder>
  project?: Project
  onClose: () => void
  editingTag?: Tag
}

export const TAG_DRAFT_KEY = 'daydaco_tag_draft'

const AddTagModal: React.FC<AddTagModalProps> = ({
  onClose,
  workorder,
  project,
  editingTag,
}) => {
  const navigate = useNavigate()
  const { user } = useUser()
  const { createTag, patchTag, refreshTags, refreshCreatedTags } =
    useTagContext()
  const { workOrders: workOrderNotifications } = useNotificationContext()
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isStepValid, setIsStepValid] = useState<boolean>(false)
  const [showErrors, setShowErrors] = useState<boolean>(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fetchedProject, setFetchedProject] = useState<Project | null>(null)
  const [contractorName, setContractorName] = useState<string>('')
  const [brokerName, setBrokerName] = useState<string>('')
  const [generatedLabel, setGeneratedLabel] = useState<string | null>(null)

  // Backend draft state - track ID of draft saved to backend
  const [backendDraftId, setBackendDraftId] = useState<number | null>(
    editingTag?.status === 'DRAFT' ? editingTag.id : null,
  )
  const [isSavingDraft, setIsSavingDraft] = useState(false)

  // Attachment state
  const [attachments, setAttachments] = useState<Attachment[]>(
    editingTag?.attachments || [],
  )
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([])

  // Initialize form data - check localStorage for draft if creating new tag
  const getInitialFormData = (): Partial<CreateTagData> => {
    // When editing an existing tag (including drafts), use its data
    if (editingTag) {
      // Extract workorder_id and project_id from editingTag
      const editingWorkorderId =
        typeof editingTag.workorder === 'object'
          ? editingTag.workorder?.id
          : editingTag.workorder
      const editingProjectId =
        typeof editingTag.project === 'object'
          ? (editingTag.project as { id: number })?.id
          : editingTag.project

      return {
        ...Object.fromEntries(
          Object.entries(editingTag).map(([key, value]) => [
            key,
            value ?? undefined,
          ]),
        ),
        created_by_id: user?.id,
        workorder_id: workorder?.id ? Number(workorder.id) : editingWorkorderId,
        project_id: project?.id || editingProjectId,
        assigned_user_id:
          typeof editingTag.assigned_user === 'object' &&
          editingTag.assigned_user
            ? editingTag.assigned_user.id
            : editingTag.assigned_user,
      } as Partial<CreateTagData>
    }

    // Try to load draft from localStorage for new tags
    try {
      const savedDraft = localStorage.getItem(TAG_DRAFT_KEY)
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft)
        // Only use draft if it's for the same workorder/project (or no workorder/project specified)
        const matchesContext =
          (!workorder?.id ||
            parsed.workorder_id === Number(workorder.id) ||
            !parsed.workorder_id) &&
          (!project?.id ||
            parsed.project_id === project.id ||
            !parsed.project_id)

        if (matchesContext) {
          console.log('Loaded tag draft from localStorage')
          return {
            ...parsed,
            workorder_id: workorder?.id
              ? Number(workorder.id)
              : parsed.workorder_id,
            project_id: project?.id || parsed.project_id,
            created_by_id: user?.id,
          }
        }
      }
    } catch (error) {
      console.error('Failed to load tag draft:', error)
    }

    return {
      created_date: new Date().toISOString(),
      updated_date: new Date().toISOString(),
      created_by_id: user?.id,
      workorder_id: workorder?.id ? Number(workorder.id) : undefined,
      project_id: project?.id,
      entity: 'Tag',
      origin_address:
        workorder?.pickup_address || project?.pickup_address || '',
      origin_name: '',
      destination_address:
        workorder?.dropoff_address || project?.dropoff_address || '',
      title: '',
      customer: '',
      material: workorder?.material || project?.material || '',
      start_date: undefined,
      load_info: {},
      additional_expenses: {},
      pay_rate: workorder?.pay_rate || project?.pay_rate || '',
    }
  }

  const [formData, setFormData] =
    useState<Partial<CreateTagData>>(getInitialFormData)

  // Backend auto-save callback - saves draft to server
  const handleBackendAutoSave = useCallback(
    async (data: Partial<CreateTagData>) => {
      // Don't auto-save to backend if we don't have the generated label yet
      // or if we're editing an existing non-draft tag
      if (!generatedLabel) return
      if (editingTag && editingTag.status !== 'DRAFT') return
      if (!user?.id) return

      // Don't save if we don't have enough data
      if (!data.workorder_id && !data.project_id) return

      try {
        const draftData = {
          ...data,
          label: generatedLabel,
          created_by_id: user.id,
          entity: 'Tag',
          project_id: fetchedProject?.id || project?.id || data.project_id,
          workorder_id: workorder?.id
            ? Number(workorder.id)
            : data.workorder_id,
        }

        if (backendDraftId) {
          // Update existing draft
          await tagService.updateDraft(String(backendDraftId), draftData)
        } else {
          // Create new draft
          const saved = await tagService.saveDraft(draftData)
          setBackendDraftId(saved.id)
        }
      } catch (error) {
        console.error('Failed to auto-save draft to backend:', error)
      }
    },
    [
      generatedLabel,
      editingTag,
      user?.id,
      fetchedProject?.id,
      project?.id,
      workorder?.id,
      backendDraftId,
    ],
  )

  // Use debounced auto-save hook (only for new tags, not editing non-draft tags)
  const {
    status: autoSaveStatus,
    lastSaved: autoSaveLastSaved,
    clearDraft,
  } = useAutoSave({
    storageKey: TAG_DRAFT_KEY,
    data: formData,
    enabled: !editingTag || editingTag.status === 'DRAFT',
    debounceMs: 3000, // 3 second debounce as per requirements
    onServerSave: handleBackendAutoSave,
  })

  // Handler for explicit "Save Draft" button
  const handleSaveDraft = useCallback(async () => {
    if (!user?.id) {
      toast.error('No user found')
      return
    }

    if (!formData.workorder_id && !formData.project_id && !fetchedProject?.id) {
      toast.error('Either workorder or project is required')
      return
    }

    setIsSavingDraft(true)
    try {
      const draftData = {
        ...formData,
        label: generatedLabel || undefined,
        created_by_id: user.id,
        entity: 'Tag',
        project_id: fetchedProject?.id || project?.id || formData.project_id,
        workorder_id: workorder?.id
          ? Number(workorder.id)
          : formData.workorder_id,
      }

      if (backendDraftId) {
        // Update existing draft
        await tagService.updateDraft(String(backendDraftId), draftData)
        toast.success('Draft saved')
      } else {
        // Create new draft
        const saved = await tagService.saveDraft(draftData)
        setBackendDraftId(saved.id)
        toast.success('Draft saved')
      }

      // Clear localStorage draft
      clearDraft()

      // Refresh tags to include the new draft
      await refreshTags(true)

      // Navigate to drafts page
      navigate('/tags/drafts')
      onClose()
    } catch (error) {
      console.error('Failed to save draft:', error)
      toast.error('Failed to save draft')
    } finally {
      setIsSavingDraft(false)
    }
  }, [
    user?.id,
    formData,
    generatedLabel,
    fetchedProject?.id,
    project?.id,
    workorder?.id,
    backendDraftId,
    clearDraft,
    refreshTags,
    navigate,
    onClose,
  ])

  // Fetch project data when workorder changes
  useEffect(() => {
    const fetchProject = async () => {
      if (workorder?.project) {
        try {
          const projectId =
            typeof workorder.project === 'object'
              ? workorder.project.id
              : workorder.project
          const projectData = await projectService.getProject(projectId)
          setFetchedProject(projectData)
          // Update form data with project info
          setFormData((prev) => ({
            ...prev,
            project_id: projectData.id,
            material: projectData.material || prev.material,
            origin_address: projectData.pickup_address || prev.origin_address,
            destination_address:
              projectData.dropoff_address || prev.destination_address,
          }))
        } catch (error) {
          console.error('Failed to fetch project:', error)
        }
      }
    }
    fetchProject()
  }, [workorder])

  // Fetch contractor and broker names
  useEffect(() => {
    const fetchNames = async () => {
      if (workorder || project || fetchedProject) {
        try {
          // Create a mock tag object to use with the service functions
          const mockTag: Partial<Tag> = {
            workorder: workorder?.id ? Number(workorder.id) : undefined,
            project: project?.id || fetchedProject?.id,
            broker: workorder?.created_by?.id,
          }

          const [contractor, broker] = await Promise.all([
            getRelatedContractorName(mockTag as Tag),
            getRelatedBrokerName(mockTag as Tag),
          ])
          setContractorName(contractor)
          setBrokerName(broker)
        } catch (error) {
          console.error('Error fetching contractor and broker names:', error)
          setContractorName('-')
          setBrokerName('-')
        }
      }
    }

    fetchNames()
  }, [workorder, project, fetchedProject])

  // Fetch generated tag label when modal opens (only for new tags)
  useEffect(() => {
    const fetchGeneratedLabel = async () => {
      if (!editingTag) {
        try {
          const { label } = await tagService.generateLabel()
          setGeneratedLabel(label)
        } catch (error) {
          console.error('Failed to generate tag label:', error)
        }
      } else {
        // For editing, use the existing tag's label
        setGeneratedLabel(editingTag.label)
      }
    }
    fetchGeneratedLabel()
  }, [editingTag])

  useEffect(() => {
    if (workorder) {
      setFormData((prev) => ({
        ...prev,
        material: workorder.material || prev.material,
        origin_address: workorder.pickup_address || prev.origin_address,
        destination_address:
          workorder.dropoff_address || prev.destination_address,
      }))
    } else if (project) {
      setFormData((prev) => ({
        ...prev,
        material: project.material || prev.material,
        origin_address: project.pickup_address || prev.origin_address,
        destination_address:
          project.dropoff_address || prev.destination_address,
      }))
    }
  }, [workorder, project])

  useEffect(() => {
    if (editingTag) {
      // Extract project_id and workorder_id from editingTag if not provided via props
      const editingWorkorderId =
        typeof editingTag.workorder === 'object'
          ? editingTag.workorder?.id
          : editingTag.workorder
      const editingProjectId =
        typeof editingTag.project === 'object'
          ? (editingTag.project as { id: number })?.id
          : editingTag.project

      setFormData({
        ...Object.fromEntries(
          Object.entries(editingTag).map(([key, value]) => [
            key,
            value ?? undefined,
          ]),
        ),
        workorder_id: workorder?.id ? Number(workorder.id) : editingWorkorderId,
        project_id: project?.id || editingProjectId,
        assigned_user_id:
          typeof editingTag.assigned_user === 'object' &&
          editingTag.assigned_user
            ? editingTag.assigned_user.id
            : editingTag.assigned_user,
      } as Partial<CreateTagData>)
    }
  }, [editingTag, workorder?.id, project?.id])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      if (!formData.assigned_user_id) {
        throw new Error('Please assign the tag to someone')
      }

      if (!user?.id) {
        throw new Error('No user found')
      }

      // If editing a draft, send it instead of patching
      if (editingTag && editingTag.status === 'DRAFT') {
        // Update the draft with latest data first
        await tagService.updateDraft(String(editingTag.id), {
          ...formData,
          assigned_user_id: formData.assigned_user_id,
        } as Partial<Tag>)

        // Then send the draft (transition to NEW)
        await tagService.sendDraft(String(editingTag.id))

        // Clear draft from localStorage after successful send
        clearDraft()

        setIsSubmitting(false)
        // Navigate to open tags after successful send
        navigate('/tags/open')
        onClose()
        return
      }

      // If we have a backend draft ID (auto-saved), send it
      if (backendDraftId) {
        // Update the draft with latest data first
        await tagService.updateDraft(String(backendDraftId), {
          ...formData,
          assigned_user_id: formData.assigned_user_id,
        } as Partial<Tag>)

        // Then send the draft (transition to NEW)
        await tagService.sendDraft(String(backendDraftId))

        // Clear draft from localStorage after successful send
        clearDraft()

        setIsSubmitting(false)
        // Navigate to open tags after successful send
        navigate('/tags/open')
        onClose()
        return
      }

      if (editingTag) {
        await patchTag(String(editingTag.id), formData as Partial<Tag>)
        await Promise.all([refreshCreatedTags()])
      } else {
        const tagData = {
          ...formData,
          created_by_id: user.id,
          assigned_user_id: formData.assigned_user_id,
          entity: 'Tag',
          project_id: fetchedProject?.id || project?.id, // Use fetched project if available
          workorder_id: workorder?.id ? Number(workorder.id) : undefined,
          load_info: {},
          additional_expenses: {},
        } as CreateTagData

        await createTag(tagData)

        // Mark work order notification as read when tag is created from it
        if (workorder?.id) {
          try {
            await workOrderNotifications.markAsRead(Number(workorder.id))
          } catch (error) {
            console.error(
              'Failed to mark work order notification as read:',
              error,
            )
            // Don't show error to user as this is not critical
          }
        }

        // Clear draft from localStorage after successful save
        clearDraft()

        setIsSubmitting(false)
        // Navigate to open tags after successful send
        navigate('/tags/open')
        onClose()
        return
      }

      // Clear draft from localStorage after successful save (for edit case)
      clearDraft()

      setIsSubmitting(false)
      // Redirect to open tags page after successful edit
      navigate('/tags/open')
      onClose()
    } catch (error) {
      console.error(`Failed to ${editingTag ? 'update' : 'create'} tag:`, error)
      setIsSubmitting(false)
    }
  }

  const handleNext = () => {
    if (step < 3) {
      if (!isStepValid) {
        setShowErrors(true)
        return
      }
      setShowErrors(false)
      setStep(step + 1)
    } else {
      handleSubmit()
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const updateFormData = (data: Partial<CreateTagData>) => {
    setFormData((prev) => ({ ...prev, ...data }))
  }

  // Handle immediate upload when we have a backend draft
  const handleAttachmentUpload = useCallback(
    async (files: File[]) => {
      const draftId = backendDraftId || (editingTag?.id ?? null)
      if (draftId) {
        // Upload immediately to existing draft
        const uploaded = await tagService.uploadAttachments(
          String(draftId),
          files,
        )
        setAttachments((prev) => [...prev, ...uploaded])
      } else {
        // Queue for upload after draft creation
        setPendingAttachments((prev) => [...prev, ...files])
      }
    },
    [backendDraftId, editingTag?.id],
  )

  // Handle attachment deletion
  const handleAttachmentDelete = useCallback(
    async (attachmentId: number) => {
      const draftId = backendDraftId || (editingTag?.id ?? null)
      if (draftId) {
        await tagService.deleteAttachment(String(draftId), attachmentId)
        setAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
      }
    },
    [backendDraftId, editingTag?.id],
  )

  // Upload pending attachments when draft is created
  useEffect(() => {
    const uploadPending = async () => {
      if (backendDraftId && pendingAttachments.length > 0) {
        try {
          const uploaded = await tagService.uploadAttachments(
            String(backendDraftId),
            pendingAttachments,
          )
          setAttachments((prev) => [...prev, ...uploaded])
          setPendingAttachments([])
        } catch (error) {
          console.error('Failed to upload pending attachments:', error)
        }
      }
    }
    uploadPending()
  }, [backendDraftId, pendingAttachments])

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <ContactInfoStep
            onUpdate={updateFormData}
            data={formData}
            onValidationChange={setIsStepValid}
            showErrors={showErrors}
          />
        )
      case 2:
        return (
          <JobDetailsStep
            onUpdate={updateFormData}
            data={formData}
            relatedWorkorder={workorder}
            relatedProject={fetchedProject || project}
            onValidationChange={(isValid, errorMessage) => {
              setIsStepValid(isValid)
              setValidationError(errorMessage || null)
            }}
            showErrors={showErrors}
            attachments={attachments}
            pendingAttachments={pendingAttachments}
            onPendingAttachmentsChange={setPendingAttachments}
            onAttachmentUpload={
              backendDraftId || editingTag?.id
                ? handleAttachmentUpload
                : undefined
            }
            onAttachmentDelete={
              backendDraftId || editingTag?.id
                ? handleAttachmentDelete
                : undefined
            }
          />
        )
      case 3:
        return (
          <TagConfirmation
            data={{
              title: formData.title,
              origin_name: formData.origin_name,
              origin_address: formData.origin_address,
              destination_address: formData.destination_address,
              material: formData.material,
              rate_type: formData.rate_type,
              pay_rate: formData.pay_rate,
              notes: formData.notes,
              start_date: formData.start_date,
              assigned_user_id: formData.assigned_user_id,
              assigned_user: formData.assigned_user,
              shared_emails: [],
              contractor: contractorName,
              broker: brokerName,
              project_label: fetchedProject?.label || project?.label,
              workorder_label: workorder?.label,
            }}
            attachments={attachments}
            pendingAttachments={pendingAttachments}
          />
        )
      default:
        return null
    }
  }

  // Determine if editing a draft
  const isEditingDraft = editingTag?.status === 'DRAFT'

  return (
    <DashboardAddModal
      onClose={onClose}
      onSubmit={handleSubmit}
      onNext={handleNext}
      onBack={handleBack}
      title={
        isEditingDraft ? 'Continue Draft' : editingTag ? 'Edit Tag' : 'New Tag'
      }
      projectId={generatedLabel || undefined}
      step={step}
      totalSteps={3}
      isSubmitting={isSubmitting}
      onClearDraft={clearDraft}
      autoSaveStatus={
        !editingTag || isEditingDraft ? autoSaveStatus : undefined
      }
      autoSaveLastSaved={
        !editingTag || isEditingDraft ? autoSaveLastSaved : undefined
      }
      onSaveDraft={handleSaveDraft}
      showSaveDraft={!editingTag || isEditingDraft}
      isSavingDraft={isSavingDraft}
      validationError={showErrors ? validationError : null}
      sendConfirmTitle="Send Tag"
      sendConfirmMessage="Are you certain you want to send this Tag? Once sent, this Tag cannot be called back or modified."
      cancelConfirmTitle="Delete Tag"
      cancelConfirmMessage="Are you certain you want to permanently delete this Tag? It will no longer be available or accessible to you."
      footerVariant="tag"
    >
      {renderStep()}
    </DashboardAddModal>
  )
}

export default AddTagModal
